import classNames from 'classnames';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  MenubarContext,
  MenuOpenContext,
  ParentMenuContext
} from '../../../../components/Menubar/contexts';
import { getCurrentClassroomId, getIsAdmin } from '../../selectors/users';
import { setCurrentClassroom } from '../../../User/actions';
import {
  listMyClassrooms,
  switchCurrentClassroom
} from '../../../../firebase/classrooms';
import NavFlyout from './NavFlyout';

/**
 * "My classroom": a flyout listing every classroom the signed-in user belongs to (admins see all),
 * with the current one highlighted; picking one makes it current. Staff-only, because the
 * `classrooms` read rule denies students — Nav renders it inside the isStaff-gated slot.
 *
 * Whether the item exists at all depends on the classroom count, so unlike the sibling flyouts it
 * can't wait for its own hover to load: it fetches once the parent Classroom menu opens (not on
 * mount — that would query Firestore on every page load), and renders nothing below two classrooms.
 */
const MyClassroomMenu = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isAdmin = useSelector(getIsAdmin);
  const currentClassroomId = useSelector(getCurrentClassroomId);
  const { setMenuOpen, createMenuItemHandlers } = useContext(MenubarContext);
  const parentMenu = useContext(ParentMenuContext);
  const openMenu = useContext(MenuOpenContext);
  const [classrooms, setClassrooms] = useState(null); // null = not loaded yet
  const loadedKey = useRef(null);

  useEffect(() => {
    if (openMenu !== parentMenu) return;
    // Re-fetch after a create/join (which switches the current classroom), so a room the user just
    // gained shows up without a reload; otherwise the list is fetched once per menu lifetime.
    const key = `${isAdmin}:${currentClassroomId}`;
    if (loadedKey.current === key) return;
    loadedKey.current = key;
    listMyClassrooms(isAdmin)
      .then(setClassrooms)
      .catch(() => setClassrooms([]));
  }, [openMenu, parentMenu, isAdmin, currentClassroomId]);

  // Same borrowed Menubar focus/blur handlers as ClassroomStudentsMenu: focusing our own trigger
  // must cancel the Menubar's pending hide-timeout, or the menu closes right after the flyout opens.
  const itemHandlers = createMenuItemHandlers
    ? createMenuItemHandlers(parentMenu)
    : {};
  const triggerProps = {
    onFocus: itemHandlers.onFocus,
    onBlur: itemHandlers.onBlur
  };

  const select = (classroom) => {
    setMenuOpen('none');
    if (classroom.id === currentClassroomId) return;
    switchCurrentClassroom(classroom.id, classroom.name)
      .then(() => dispatch(setCurrentClassroom(classroom.id, classroom.name)))
      .catch(() => {
        // Leave Redux on the old classroom: the profile doc still points there.
      });
  };

  if (!classrooms || classrooms.length < 2) return null;

  return (
    <NavFlyout
      label={t('Nav.Classroom.Mine', { defaultValue: 'My classroom' })}
      triggerProps={triggerProps}
    >
      {classrooms.map((classroom) => {
        const isCurrent = classroom.id === currentClassroomId;
        return (
          <li
            key={classroom.id}
            className={classNames(
              'nav__dropdown-item',
              isCurrent && 'nav__dropdown-item--current'
            )}
          >
            <button
              type="button"
              role="menuitem"
              tabIndex={-1}
              aria-current={isCurrent}
              // Plain left mousedown is prevented so focus stays on the flyout trigger — otherwise
              // its blur fires the Menubar's ~10ms hide-timeout and this button is unmounted before
              // the click lands (same fix as the sketch links in ClassroomStudentsMenu).
              onMouseDown={(e) => {
                if (
                  e.button === 0 &&
                  !e.metaKey &&
                  !e.ctrlKey &&
                  !e.shiftKey &&
                  !e.altKey
                ) {
                  e.preventDefault();
                }
              }}
              onClick={() => select(classroom)}
            >
              {classroom.name}
            </button>
          </li>
        );
      })}
    </NavFlyout>
  );
};

export default MyClassroomMenu;
